import { useCallback } from 'react';
import { defineMessages, type MessageValue, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@sg/garnish';

import { type BundleMachineTypes, type BundleSelectProduct } from '@order/components-menu';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shows one notice for the whole bundle once every product has settled.
 */
export const useBundleAddedToBagNotice = (params: Params) => {
  const { productsBySlug } = params;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  return useCallback(
    (summary: AddToBagSummary) => {
      const { addedCount, failedProductSlugs } = summary;

      const failedCount = failedProductSlugs.length;
      const hasFailedProducts = failedCount > 0;
      const text = hasFailedProducts
        ? formatMessage(messages.someAdded, {
            added_number: addedCount,
            total_number: addedCount + failedCount,
            failed_product_names: getProductNames({
              productSlugs: failedProductSlugs,
              productsBySlug,
            }),
          })
        : formatMessage(messages.allAdded, { added_number: addedCount });

      addNoticeBanner({
        palette: hasFailedProducts ? 'neutral' : 'success',
        text,
        autoHideTimeout: hasFailedProducts
          ? PARTIAL_NOTICE_AUTO_HIDE_TIMEOUT
          : SUCCESS_NOTICE_AUTO_HIDE_TIMEOUT,
      });
    },
    [addNoticeBanner, formatMessage, productsBySlug],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Names each product once, even when it was picked more than once.
 */
function getProductNames(params: GetProductNamesParams) {
  const { productSlugs, productsBySlug } = params;

  const uniqueProductSlugs = [...new Set(productSlugs)];

  return uniqueProductSlugs
    .map((productSlug) => productsBySlug.get(productSlug)?.name)
    .filter(Boolean)
    .join(', ');
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SUCCESS_NOTICE_AUTO_HIDE_TIMEOUT = 3000;
const PARTIAL_NOTICE_AUTO_HIDE_TIMEOUT = 4000;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly allAdded: { readonly added_number: MessageValue };
  readonly someAdded: {
    readonly added_number: MessageValue;
    readonly failed_product_names: MessageValue;
    readonly total_number: MessageValue;
  };
}>({
  allAdded: {
    defaultMessage: '{added_number} products added to your bag.',
    description: 'Bundle | Added to bag notice | Every product added',
  },
  someAdded: {
    defaultMessage:
      '{added_number} of {total_number} products added. We couldn’t add {failed_product_names}.',
    description: 'Bundle | Added to bag notice | Some products added',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  productsBySlug: Map<string, BundleSelectProduct>;
};

type GetProductNamesParams = {
  productSlugs: string[];
  productsBySlug: Map<string, BundleSelectProduct>;
};

type AddToBagSummary = BundleMachineTypes.AddToBagSummary;
